import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { API_CONFIG } from '../config/api';

// Create axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: API_CONFIG.FULL_URL,
  timeout: API_CONFIG.TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    const siteId = localStorage.getItem('site_id');
    if (siteId) {
      config.headers['X-Site-ID'] = siteId;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling and token refresh
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Handle 401 Unauthorized - Token expired
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refresh_token');
        if (refreshToken) {
          const response = await axios.post(`${API_CONFIG.FULL_URL}/auth/refresh`, {
            refresh_token: refreshToken,
          });

          const { access_token, refresh_token: newRefreshToken } = response.data;

          localStorage.setItem('access_token', access_token);
          if (newRefreshToken) {
            localStorage.setItem('refresh_token', newRefreshToken);
          }

          originalRequest.headers.Authorization = `Bearer ${access_token}`;
          return apiClient(originalRequest);
        }
      } catch (refreshError) {
        // Clear tokens and redirect to login
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user_data');
        localStorage.removeItem('site_id');
        window.location.href = '/login';
      }
    }

    return Promise.reject(error);
  }
);

// Generic API methods — T defaults to any for convenience in page components
export const api = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  get: async <T = any>(url: string, config = {}): Promise<T> => {
    const response = await apiClient.get<T>(url, config);
    return response.data;
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  post: async <T = any>(url: string, data = {}): Promise<T> => {
    const response = await apiClient.post<T>(url, data);
    return response.data;
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  put: async <T = any>(url: string, data = {}): Promise<T> => {
    const response = await apiClient.put<T>(url, data);
    return response.data;
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  patch: async <T = any>(url: string, data = {}): Promise<T> => {
    const response = await apiClient.patch<T>(url, data);
    return response.data;
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  delete: async <T = any>(url: string): Promise<T> => {
    const response = await apiClient.delete<T>(url);
    return response.data;
  },
};

export default apiClient;
